import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  FormHelperText
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import palette from 'src/theme/palette';
import { toast } from 'react-toastify';
import useMediaQuery from '@mui/material/useMediaQuery';

// Rich Text Editor imports
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const PrivacyPolicyModal = ({ open, onClose, onSubmit, policy, isEditing }) => {
  const isMobile = useMediaQuery('(max-width:600px)');
  const [content, setContent] = useState('');
  const [error, setError] = useState('');

  // Rich Text Editor modules configuration
  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ font: [] }],
      [{ size: [] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ color: [] }, { background: [] }],
      [{ script: 'sub' }, { script: 'super' }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ align: [] }],
      ['blockquote', 'code-block'],
      ['link', 'image', 'video'],
      ['clean']
    ],
    clipboard: {
      matchVisual: false
    }
  };

  const formats = [
    'header',
    'font',
    'size',
    'bold',
    'italic',
    'underline',
    'strike',
    'color',
    'background',
    'script',
    'list',
    'bullet',
    'indent',
    'align',
    'blockquote',
    'code-block',
    'link',
    'image',
    'video'
  ];

  // Reset form when modal opens/closes or when policy changes
  useEffect(() => {
    if (open) {
      if (isEditing && policy) {
        if (policy.contents && Array.isArray(policy.contents)) {
          setContent(policy.contents[0]?.content || policy.contents[0]?.paragraph || '');
        } else {
          setContent(policy.paragraph || policy.content || '');
        }
      } else {
        setContent('');
      }
      setError('');
    }
  }, [open, isEditing, policy]);

  const handleContentChange = (value) => {
    setContent(value);

    // Validate content
    const error = validateContentField(value);
    setError(error);
  };

  const validateContentField = (value) => {
    const strippedValue = value?.replace(/<[^>]*>/g, '').trim();
    return strippedValue ? '' : 'Content is required';
  };

  const handleSubmit = () => {
    const validationError = validateContentField(content);
    setError(validationError);

    if (validationError) {
      toast.error('Please fill the content field');
      return;
    }

    const submissionData = {
      contents: [
        {
          content: content,
          type: 'Paragraph'
        }
      ]
    };

    onSubmit(submissionData);
  };

  const handleClose = () => {
    setContent('');
    setError('');
    onClose();
  };

  const isFormValid = () => {
    const strippedContent = content?.replace(/<[^>]*>/g, '').trim();
    return strippedContent;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          maxHeight: isMobile ? '100vh' : '90vh',
          margin: isMobile ? 0 : undefined
        }
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          background: palette.primary.main,
          color: palette.primary.contrastText,
          py: 2,
          px: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          zIndex: 1
        }}
      >
        <Typography
          sx={{
            fontSize: 20,
            fontFamily: 'poppinsSemiBold',
            color: palette.primary.contrastText
          }}
        >
          {isEditing ? 'Edit Privacy Policy' : 'Add New Privacy Policy'}
        </Typography>
        <IconButton
          onClick={handleClose}
          sx={{
            color: palette.primary.contrastText,
            '&:hover': {
              backgroundColor: palette.primary.dark
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 3 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontFamily: 'poppinsSemiBold',
              color: palette.primary.titledark,
              mb: 2
            }}
          >
            Privacy Policy Content *
          </Typography>

          <Box
            sx={{
              border: error
                ? `1px solid ${palette.error.main}`
                : `1px solid ${palette.primary.inputborder}`,
              borderRadius: 1,
              overflow: 'hidden',
              '& .ql-toolbar': {
                border: 'none',
                borderBottom: `1px solid ${palette.primary.inputborder}`,
                backgroundColor: palette.background.neutral,
                '& .ql-picker-label': {
                  fontFamily: 'poppinsRegular'
                },
                '& .ql-picker-options': {
                  fontFamily: 'poppinsRegular'
                }
              },
              '& .ql-container': {
                border: 'none',
                fontFamily: 'poppinsRegular',
                fontSize: '14px',
                minHeight: '400px'
              },
              '& .ql-editor': {
                minHeight: '400px',
                fontFamily: 'poppinsRegular',
                '& h1, & h2, & h3, & h4, & h5, & h6': {
                  fontFamily: 'poppinsSemiBold',
                  marginTop: '8px',
                  marginBottom: '8px'
                },
                '& a': {
                  color: palette.primary.main,
                  textDecoration: 'underline',
                  '&:hover': {
                    color: palette.primary.dark
                  }
                },
                '& img': {
                  cursor: 'pointer',
                  borderRadius: '4px',
                  transition: 'all 0.3s ease',
                  maxWidth: '100%',
                  height: 'auto',
                  margin: '8px 0',
                  '&:hover': {
                    transform: 'scale(1.02)',
                    boxShadow: `0 4px 12px ${palette.primary.main}40`
                  }
                },
                '&.ql-blank::before': {
                  color: palette.text.disabled,
                  fontStyle: 'normal',
                  fontFamily: 'poppinsRegular'
                }
              },
              '&:hover': {
                borderColor: palette.primary.main
              },
              '&:focus-within': {
                borderColor: palette.primary.main,
                borderWidth: 2
              }
            }}
          >
            <ReactQuill
              value={content}
              onChange={handleContentChange}
              modules={modules}
              formats={formats}
              placeholder="Enter privacy policy content..."
              theme="snow"
            />
          </Box>

          {error && (
            <FormHelperText
              error
              sx={{
                ml: 0,
                mt: 1
              }}
            >
              {error}
            </FormHelperText>
          )}
        </Box>
      </DialogContent>

      {/* Footer Buttons */}
      <DialogActions sx={{ p: 3, gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          onClick={handleClose}
          sx={{
            borderColor: palette.primary.inputborder,
            color: palette.primary.title,
            '&:hover': {
              borderColor: palette.primary.main,
              backgroundColor: palette.primary.hover
            }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isFormValid()}
          sx={{
            backgroundColor: palette.primary.main,
            color: palette.primary.contrastText,
            '&:hover': {
              backgroundColor: palette.primary.dark
            },
            '&:disabled': {
              backgroundColor: palette.primary.disabled,
              color: palette.text.disabled
            }
          }}
        >
          {isEditing ? 'Update' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrivacyPolicyModal;
